/**
 * The automatic end of an effect — «до начала следующего хода» of the creature of the sheet it sits on
 * (docs/backlog/tia-turn-clock-design-2026-10-28.md, docs/backlog/tia-effect-expiration-implementation-2026-10-28.md).
 * Pure helpers: reading a stored expiry, its state against the fight clock, and the words for it. No token id, no
 * fight id ever reaches a label.
 */
import type { EffectExpiryPause, EffectTurnStartExpiry, TurnClock } from './domain.js';

const PAUSES: readonly EffectExpiryPause[] = ['anchor-removed', 'anchor-ambiguous', 'anchor-skipped', 'clock-full', 'clock-rewound', 'fight-ended'];

/** A stored expiry, checked: well-formed or none (an effect then ends by hand). Idempotent. */
export function readEffectExpiry(raw: unknown): EffectTurnStartExpiry | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  if (r.kind !== 'turn-start' || typeof r.fightId !== 'string' || r.fightId === '') return undefined;
  if (typeof r.expectRound !== 'number' || !Number.isInteger(r.expectRound) || r.expectRound < 1) return undefined;
  if (typeof r.atSeq !== 'number' || !Number.isInteger(r.atSeq) || r.atSeq < 0) return undefined;
  const paused = PAUSES.find((p) => p === r.paused);
  return { kind: 'turn-start', fightId: r.fightId, expectRound: r.expectRound, atSeq: r.atSeq, ...(paused ? { paused } : {}) };
}

export type EffectExpiryState = { state: 'waiting' } | { state: 'paused'; reason: EffectExpiryPause };

/**
 * Where the expiry stands against the current fight clock: a stored pause; else derived — no clock or another fight
 * (`fight-ended`), a clock older than the one it was set on (`clock-rewound`, e.g. a selective restore); else waiting.
 */
export function effectExpiryState(expiry: EffectTurnStartExpiry, clock: TurnClock | undefined): EffectExpiryState {
  if (expiry.paused) return { state: 'paused', reason: expiry.paused };
  if (!clock || clock.fightId !== expiry.fightId) return { state: 'paused', reason: 'fight-ended' };
  if (clock.seq < expiry.atSeq) return { state: 'paused', reason: 'clock-rewound' };
  if (clock.full) return { state: 'paused', reason: 'clock-full' };
  return { state: 'waiting' };
}

export const EFFECT_EXPIRY_PAUSE_RU: Record<EffectExpiryPause, string> = {
  'anchor-removed': 'участника нет в бою',
  'anchor-ambiguous': 'участник боя не определён однозначно',
  'anchor-skipped': 'ход прошёл без начала',
  'clock-full': 'слишком много участников для учёта ходов',
  'clock-rewound': 'бой откатился раньше назначения срока',
  'fight-ended': 'бой окончен',
};

export const EFFECT_EXPIRY_PAUSE_EN: Record<EffectExpiryPause, string> = {
  'anchor-removed': 'its creature is not in the fight',
  'anchor-ambiguous': 'its creature in the fight is not unique',
  'anchor-skipped': 'its turn passed without a counted start',
  'clock-full': 'too many participants to count turns',
  'clock-rewound': 'the fight was restored to before the expiry was set',
  'fight-ended': 'the fight ended',
};

/** The HUD words (the creature is the sheet's own: «своего»). */
export function effectExpiryLabelRu(expiry: EffectTurnStartExpiry, st: EffectExpiryState): string {
  if (st.state === 'waiting') return `до начала своего хода (раунд ${expiry.expectRound})`;
  return `авто-срок на паузе: ${EFFECT_EXPIRY_PAUSE_RU[st.reason]}`;
}

/** The model's / receipt words. */
export function effectExpiryTextEn(sheetName: string, expiry: EffectTurnStartExpiry, st: EffectExpiryState): string {
  if (st.state === 'waiting') return `ends automatically at the start of ${sheetName}'s next turn (round ${expiry.expectRound})`;
  return `automatic end paused (${EFFECT_EXPIRY_PAUSE_EN[st.reason]}) — the GM must clear or set it again; until then it is removed by hand`;
}
